import { verifyPhoneNumberRequestSchema } from "@/constants/zod-schemas";
import { useCheckPhoneNumber, useVerifyPhoneNumber } from "@/hooks/query-hooks/useAuth";
import { Button, Form, Input } from "antd";
import { useEffect, useState } from "react";

const OTP_EXPIRATION_TIME: number = 30; // 30 seconds

export function AskOTPCode({
    requestId,
    phone,
    handleNextStep,
    onUpdateRequestId
}: {
    requestId: number;
    phone: string;
    handleNextStep: () => void;
    onUpdateRequestId: (data: CheckPhoneNumberResponse) => void;
}) {

    const [form] = Form.useForm();

    const { mutate, isMutating } = useVerifyPhoneNumber();
    const onFinish = (values: { code: string }) => {
        const data: VerifyPhoneNumberRequest = {
            requestId,
            phone,
            code: values.code,
        };
        const payload = verifyPhoneNumberRequestSchema.parse(data);
        mutate(payload, {
            onSuccess: () => {
                handleNextStep();
            }
        })

    };

    const [timer, setTimer] = useState(OTP_EXPIRATION_TIME);
    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    const { mutate: requestOTP, isMutating: isRequesting } = useCheckPhoneNumber();
    const onRequestOTPAgain = () => {
        requestOTP({ phone }, {
            onSuccess: (data) => {
                // Reset timer when requesting a new OTP
                setTimer(OTP_EXPIRATION_TIME);
                onUpdateRequestId(data);
                form.resetFields();
            }
        });
    }

    return (
        <>
            <article
                className="font-poppins space-y-2"
            >
                <h3
                    className="text-lg font-medium text-center"
                >
                    Enter OTP Code
                </h3>
                <p className="text-sm text-center text-gray-OTP_EXPIRATION_TIME0">
                    Please enter the OTP code sent to {phone}.
                </p>
            </article>
            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
            >
                <Form.Item
                    label="OTP Code"
                    name="code"
                    rules={[
                        {
                            required: true,
                            message: "Required!"
                        },
                        {
                            len: 6,
                            message: "Invalid OTP code!"
                        }
                    ]}
                >
                    <Input.OTP
                        length={6}
                        separator="-"
                        size="large"
                        formatter={(value) => value.replace(/[^0-9]/g, '')}
                    />
                </Form.Item>

                <Button
                    block
                    size="large"
                    htmlType="submit"
                    className="bg-primary-gradient !text-white !font-poppins"
                    loading={isMutating}
                >
                    Verify
                </Button>

                <div
                    className="flex items-center justify-center gap-1 mt-6"
                >
                    <p>
                        Didn&apos;t receive the code?
                    </p>
                    <Button
                        type="link"
                        htmlType="button"
                        className="!font-poppins"
                        disabled={isRequesting || timer > 0}
                        onClick={onRequestOTPAgain}
                    >
                        Resend {timer > 0 && ` in 00:${timer < 10 ? '0' + timer : timer}s`}
                    </Button>
                </div>
            </Form>
        </>
    )
}
